import { isNull } from 'lodash';
import React, {
    ReactElement, ReactNode, useEffect, useRef, useState,
} from 'react';
import arrowRightIcon from '@/assets/icons/front/arrow-right.svg';
import Button from './Button';
import cn from 'classnames';
import Image from 'next/image';

type Props = {
    className ?: string;
    heading : ReactNode;
    content : ReactNode;
};

const Accordion : React.FC<Props> = ({ heading, content, className = '' }) : ReactElement => {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() : void => {
        if (isNull(contentRef.current)) {
            return;
        }

        const { collapsed } = contentRef.current.dataset;

        if (collapsed === 'true') {
            collapse(contentRef.current);
            contentRef.current?.classList.remove('hidden');
        }
    }, []);

    const collapse = (element : HTMLDivElement) : void => {
        setIsAnimating(true);
        const height = element.scrollHeight;
        const elementTransition = element.style.transition;

        element.style.transition = '';

        requestAnimationFrame(() => {
            element.style.height = height + 'px';
            element.style.transition = elementTransition;

            requestAnimationFrame(() => {
                element.style.height = 0 + 'px';
            });
        });

        const transitionendCallback = () => {
            clearTimeout(timeoutId);
            element.removeEventListener('transitionend', transitionendCallback);
            setIsAnimating(false);
        };

        const timeoutId = setTimeout(transitionendCallback, 500);

        element.setAttribute('data-collapsed', 'true');
        element.addEventListener('transitionend', transitionendCallback);
    };

    const expand = (element : HTMLDivElement) : void => {
        setIsAnimating(true);
        const height = element.scrollHeight;

        element.style.height = height + 'px';

        const transitionendCallback = () => {
            clearTimeout(timeoutId);
            element.removeEventListener('transitionend', transitionendCallback);
            element.style.height = '';
            setIsAnimating(false);
        };

        const timeoutId = setTimeout(transitionendCallback, 500);

        element.addEventListener('transitionend', transitionendCallback);
        element.setAttribute('data-collapsed', 'false');
    };

    const handleClick = () => {
        console.log(contentRef.current, isAnimating);

        if (isNull(contentRef.current) || isAnimating) {
            return;
        }

        if (isExpanded) {
            collapse(contentRef.current);
        } else {
            expand(contentRef.current);
        }

        setIsExpanded((isExpanded) => !isExpanded);
    };

    return (
        <div className={cn(['bg-[#F5F7FC] rounded-xl h-fit', className])}>
            <div className='flex flex-row justify-between items-center gap-7 cursor-pointer' onClick={() => handleClick()}>
                <div>
                    {heading}
                </div>

                <Button
                    className='select-none rounded-full !px-0 w-[2.25rem] xl:w-[4rem] h-[2.25rem] xl:h-[4rem] duration-300 border-0'
                    onClick={() => {}}
                    type='empty'
                >
                    <Image
                        className={`duration-300 ${isExpanded ? 'rotate-90' : 'rotate-0'}`}
                        alt='Arrow Right Icon'
                        width='48'
                        height='48'
                        src={arrowRightIcon}
                    />
                </Button>
            </div>

            {isExpanded && (
                <div className='my-[15px] border-t border-[#14274D] opacity-30' />
            )}

            <div
                ref={contentRef}
                className='h-auto overflow-hidden duration-300 hidden'
                data-collapsed='true'
            >
                {content}
            </div>
        </div>
    );
};

export default Accordion;
